// src/components/index/IndexGenerator.js - UPDATED to use centralized EvidenceDataService
import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import EvidenceDataService from '../../services/EvidenceDataService';

// Styles for the PDF index - Letter size with 1" margins and proper borders
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 72, // 1 inch margins (72 points = 1 inch)
    fontFamily: 'Times-Roman',
    size: 'LETTER', // Ensure Letter size (8.5" x 11")
  },
  titleContainer: {
    marginBottom: 20,
    alignItems: 'center',
  },
  titleLine1: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
    fontFamily: 'Times-Bold',
  },
  titleLine2: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 2,
    fontFamily: 'Times-Roman',
  },
  titleLine3: {
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Times-Roman',
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#0C141D',
    minHeight: 32, // Consistent row height
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 24, // Consistent row height
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5EA',
    borderBottomCollapse: 'collapse',
    verticalAlign: 'top', // Prevent vertical stretching
  },
  tableRowAlternate: {
    flexDirection: 'row',
    minHeight: 24, // Consistent row height
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5EA',
    borderBottomCollapse: 'collapse',
    backgroundColor: '#F6F8FA',
    verticalAlign: 'top', // Prevent vertical stretching
  },
  // Ex. column - minimal size
  cellExhibit: {
    width: '7%',
    padding: 8,
    fontSize: 12,
    fontFamily: 'Times-Roman',
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: '#E1E5EA',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#E1E5EA',
    borderCollapse: 'collapse',
    textAlign: 'center',
    verticalAlign: 'middle',
    color: '#0000FF',  // Blue color
    textDecoration: 'underline',
  },
  // Document column - fills remaining space
  cellDocument: {
    width: '49%',
    padding: 8,
    fontSize: 12,
    fontFamily: 'Times-Roman',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#E1E5EA',
    borderCollapse: 'collapse',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  // Bates column - 10pt font size and minimal width for 6-digit numbers
  cellBates: {
    width: '17%',
    padding: 8,
    fontSize: 10, // 10pt as requested
    fontFamily: 'Times-Roman',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#E1E5EA',
    borderCollapse: 'collapse',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  // Trial columns - equal size, minimal
  cellTrial: {
    width: '9%',
    padding: 2,
    paddingTop: 8,
    paddingBottom: 8,
    fontSize: 12,
    fontFamily: 'Times-Roman',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#E1E5EA',
    borderCollapse: 'collapse',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  // All headers use 12pt font (including Bates)
  headerCell: {
    fontSize: 12,
    fontFamily: 'Times-Bold',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#0C141D',
    borderCollapse: 'collapse', 
    color: '#FFFFFF',
    verticalAlign: 'middle',
    textDecoration: 'none',
  },
});

// Helper function to format bates numbers with 6-digit zero padding
const formatBatesNumber = (num) => {
  return num.toString().padStart(6, '0');
};

// Helper function to format bates range with proper 6-digit formatting
const formatBatesRange = (start, pageCount) => {
  // FIXED: Handle undefined or invalid pageCount (document still processing)
  if (!pageCount || pageCount < 1 || isNaN(pageCount)) {
    return '-';
  }
  
  if (pageCount <= 1) {
    // Single page: just show "000001" format
    return formatBatesNumber(start);
  } else {
    // Multiple pages: show "000001-000015" format
    const end = start + pageCount - 1;
    return `${formatBatesNumber(start)}-${formatBatesNumber(end)}`;
  }
};

// The PDF Document component
const IndexDocument = ({ documents, stampConfig, caseData, documentSetInfo = null }) => {
  console.log('🔍 IndexDocument - Input data:', { 
    documentsCount: documents?.length, 
    documentSetInfo
  });

  // Get party type for title
  const partyType = EvidenceDataService.getPartyTypeText(stampConfig);
  const caseName = caseData?.name || 'Unknown Case';
  const caseNumber = EvidenceDataService.getCaseNumber(caseData);

  // Filter out index documents and documents without valid page counts
  // BUT: Don't recalculate exhibit numbers - use what's already on the documents!
  const displayData = documents.filter(doc => {
    const isIndexDoc = doc.isIndex || doc.id === 'INDEX_DOCUMENT';
    const pageCount = doc.pageCount;
    const hasValidPageCount = pageCount && pageCount > 0 && !isNaN(pageCount);
    
    // Only include if it's NOT an index doc AND it HAS a valid page count
    return !isIndexDoc && hasValidPageCount;
  });
  
  console.log('🔍 IndexDocument - Filtered data:', {
    originalCount: documents.length,
    filteredCount: displayData.length,
    data: displayData.map(d => ({ 
      id: d.id,
      exhibitId: d.exhibitId,  // This should already be correct!
      startPage: d.startPage,
      pageCount: d.pageCount,
      name: d.name
    }))
  });
  
  // Convert to format needed for rendering (add batesRange formatting)
  const documentData = displayData.map(doc => ({
    ...doc,
    batesRange: formatBatesRange(doc.startPage, doc.pageCount)
  }));

  console.log('🔍 IndexDocument - Final documentData with Bates:', documentData);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Three-line title */}
        <View style={styles.titleContainer}>
          <Text style={styles.titleLine1}>{partyType}'s Exhibits</Text>
          <Text style={styles.titleLine2}>{caseName}</Text>
          <Text style={styles.titleLine3}>{caseNumber}</Text>
        </View>
        
        <View style={styles.table}>
          {/* Header Row */}
          <View style={styles.tableHeaderRow}>
            <Text style={[styles.cellExhibit, styles.headerCell]}>Ex.</Text>
            <Text style={[styles.cellDocument, styles.headerCell]}>Document</Text>
            <Text style={[styles.cellBates, styles.headerCell]}>Bates</Text>
            <Text style={[styles.cellTrial, styles.headerCell]}>ADM</Text>
            <Text style={[styles.cellTrial, styles.headerCell]}>OBJ</Text>
            <Text style={[styles.cellTrial, styles.headerCell]}>STIP</Text>
          </View>
          
          {/* Data Rows */}
          {documentData.map((doc, index) => (
            <View 
              key={doc.id} 
              style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlternate}
            >
              <Text style={styles.cellExhibit}>{doc.exhibitId}</Text>
              <Text style={styles.cellDocument}>
                {(doc.name || 'Untitled Document')}
              </Text>
              <Text style={styles.cellBates}>{doc.batesRange}</Text>
              <Text style={styles.cellTrial}></Text>
              <Text style={styles.cellTrial}></Text>
              <Text style={styles.cellTrial}></Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

/**
 * Service class for generating index documents
 */
export class IndexService {
  /**
   * Generate index PDF blob
   * @param {Array} documents - Array of document objects
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Promise<Blob>} - PDF blob
   */
  static async generateIndexPDF(documents, stampConfig, caseData, documentSetInfo = null) {
    try {
      const blob = await pdf(
        <IndexDocument 
          documents={documents} 
          stampConfig={stampConfig} 
          caseData={caseData}
          documentSetInfo={documentSetInfo}
        />
      ).toBlob();
      
      return blob;
    } catch (error) {
      console.error('Error generating index PDF:', error);
      throw error;
    }
  }

  /**
   * Generate index PDF URL for viewing
   * @param {Array} documents - Array of document objects
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Promise<string>} - URL for viewing the PDF
   */
  static async generateIndexURL(documents, stampConfig, caseData, documentSetInfo = null) {
    try {
      const blob = await this.generateIndexPDF(documents, stampConfig, caseData, documentSetInfo);
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error generating index URL:', error);
      throw error;
    }
  }

  /**
   * Create a virtual index "document" for the PDF table
   * @param {Array} documents - Array of document objects
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Object} - Virtual document object
   */
  static createIndexDocument(documents, stampConfig, caseData) {
    const partyType = EvidenceDataService.getPartyTypeText(stampConfig);
    
    return {
      id: 'INDEX_DOCUMENT',
      name: `${partyType}'s Exhibits - Index`,
      isIndex: true,
      pageCount: 1,
      status: 'completed',
      showSpinner: false,
      // Generate URL when needed
      pdfUrl: null, // Will be generated on demand
    };
  }
}

export default IndexDocument;